import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { resolvePostLoginDestination } from "@/lib/post-login-redirect.js";
import { cn } from "@/lib/utils";
import { Button } from "@repo/ui/primitives/button";
import { Card, CardContent, CardHeader } from "@repo/ui/primitives/card";
import { Input } from "@repo/ui/primitives/input";
import { Label } from "@repo/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/primitives/select";
import { useAuthStore } from "@/store";
import { authIntegration } from "@/lib/api/client";
import { api } from "@/app/config.js";
import { LoginSchema } from "@repo/contracts";
import cyberverseLogo from "@/assets/cyberverse-logo.png";
import { ForgotPasswordModal } from "@/components/auth/ForgotPasswordModal";
import { OtpCountdownHint } from "@/components/auth/OtpCountdownHint";
import { useOtpCountdown } from "@/hooks/useOtpCountdown.js";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logger } from "@/lib/app-log.js";

const EXPIRATION_OPTIONS = [
  { value: "30d", labelKey: "oneMonth" },
  { value: "90d", labelKey: "threeMonths" },
  { value: "180d", labelKey: "sixMonths" },
  { value: "365d", labelKey: "oneYear" },
];

export function LoginForm({
  className,
  onMobileSwitch,
  ...props
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, error, clearError } = useAuthStore();
  const { t } = useTranslation("login");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [formErrors, setFormErrors] = useState({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaChannel, setMfaChannel] = useState("email");
  const [mfaIssuedAt, setMfaIssuedAt] = useState(null);
  const [mfaAttemptsRemaining, setMfaAttemptsRemaining] = useState(null);
  const [passwordExpiredChallenge, setPasswordExpiredChallenge] = useState(null);
  const [expiredCurrentPassword, setExpiredCurrentPassword] = useState("");
  const [expiredNewPassword, setExpiredNewPassword] = useState("");
  const [expiredConfirmPassword, setExpiredConfirmPassword] = useState("");
  const [expiredPeriod, setExpiredPeriod] = useState("90d");
  const [ssoProviders, setSsoProviders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    authIntegration.listPublicSsoProviders().then((result) => {
      if (!cancelled && result.success) setSsoProviders(result.data || []);
    });
    return () => { cancelled = true; };
  }, []);

  const mfaCountdown = useOtpCountdown({
    issuedAt: mfaChallenge ? mfaIssuedAt : null,
    expiresInSec: mfaChallenge?.expiresInSec || 300,
    resendCooldownSec: mfaChallenge?.resendCooldownSec || 60,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (error) clearError();
  };

  const finishLoginSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => {
      navigate(resolvePostLoginDestination(location), { replace: true });
    }, 1500);
  };

  const handlePasswordExpiredResult = (result) => {
    if (result.passwordExpired && result.data) {
      setMfaChallenge(null);
      setPasswordExpiredChallenge(result.data);
      setExpiredCurrentPassword("");
      setExpiredNewPassword("");
      setExpiredConfirmPassword("");
      setExpiredPeriod("90d");
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormErrors({});
    clearError();

    const validation = LoginSchema.safeParse(formData);
    if (!validation.success) {
      const errors = {};
      validation.error?.errors?.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await authIntegration.login(formData);
      if (result.mfaRequired && result.data) {
        setMfaChallenge(result.data);
        setMfaChannel(result.data.preferredChannel || result.data.channel || "email");
        setMfaIssuedAt(Date.now());
        setMfaAttemptsRemaining(null);
        setMfaCode("");
      } else if (handlePasswordExpiredResult(result)) {
        // forced renew step
      } else if (result.success) {
        finishLoginSuccess();
      } else {
        Logger.warn("Login failed", { error: result.error });
      }
    } catch (err) {
      Logger.warn("Login error", { error: err?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (isSubmitting || !mfaChallenge?.challengeId || !mfaCode.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await authIntegration.verifyMfa({
        challengeId: mfaChallenge.challengeId,
        code: mfaCode.trim(),
        channel: mfaChannel,
      });
      if (handlePasswordExpiredResult(result)) {
        return;
      }
      if (result.success) {
        setMfaChallenge(null);
        setMfaIssuedAt(null);
        setMfaAttemptsRemaining(null);
        finishLoginSuccess();
      } else if (typeof result.attemptsRemaining === "number") {
        setMfaAttemptsRemaining(result.attemptsRemaining);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaResend = async () => {
    if (!mfaChallenge?.challengeId || !mfaCountdown.canResend) return;
    const result = await authIntegration.resendMfa({
      challengeId: mfaChallenge.challengeId,
      channel: mfaChannel,
    });
    if (result.success && result.data) {
      setMfaChallenge((prev) => ({
        ...prev,
        ...result.data,
        preferredChannel: result.data.channel,
        maskedDestination: result.data.maskedDestination,
      }));
      setMfaIssuedAt(Date.now());
      setMfaAttemptsRemaining(null);
      setMfaCode("");
    }
  };

  const handleExpiredPasswordSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !passwordExpiredChallenge?.challengeId) return;
    if (expiredNewPassword.length < 6 || expiredNewPassword !== expiredConfirmPassword) return;

    setIsSubmitting(true);
    try {
      const result = await authIntegration.changeExpiredPassword({
        challengeId: passwordExpiredChallenge.challengeId,
        currentPassword: expiredCurrentPassword,
        newPassword: expiredNewPassword,
        confirmNewPassword: expiredConfirmPassword,
        passwordExpirationPeriod: expiredPeriod,
      });
      if (result.success) {
        setPasswordExpiredChallenge(null);
        finishLoginSuccess();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const available = mfaChallenge?.availableChannels || {};
  const channelOptions = [
    available.email !== false && { value: "email", label: t("mfa.channelEmail", { defaultValue: "Email" }) },
    available.sms !== false && { value: "sms", label: t("mfa.channelSms", { defaultValue: "SMS" }) },
  ].filter(Boolean);

  const expiredFormValid =
    Boolean(expiredCurrentPassword)
    && expiredNewPassword.length >= 6
    && expiredNewPassword === expiredConfirmPassword
    && Boolean(expiredPeriod);

  let stepContent;
  if (passwordExpiredChallenge) {
    stepContent = (
      <form onSubmit={handleExpiredPasswordSubmit} className="flex flex-col gap-6">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("passwordExpired.title", { defaultValue: "Password expired" })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("passwordExpired.description", {
              defaultValue: "Enter your current password, choose a new one, and set how long it should remain valid.",
            })}
          </p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="expired-current">
            {t("passwordExpired.currentPassword", { defaultValue: "Current password" })}
          </Label>
          <Input
            id="expired-current"
            type="password"
            value={expiredCurrentPassword}
            onChange={(e) => setExpiredCurrentPassword(e.target.value)}
            className="h-[var(--ds-density-control-height)]"
            required
            autoFocus
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="expired-new">
            {t("passwordExpired.newPassword", { defaultValue: "New password" })}
          </Label>
          <Input
            id="expired-new"
            type="password"
            value={expiredNewPassword}
            onChange={(e) => setExpiredNewPassword(e.target.value)}
            className="h-[var(--ds-density-control-height)]"
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="expired-confirm">
            {t("passwordExpired.confirmPassword", { defaultValue: "Confirm new password" })}
          </Label>
          <Input
            id="expired-confirm"
            type="password"
            value={expiredConfirmPassword}
            onChange={(e) => setExpiredConfirmPassword(e.target.value)}
            className="h-[var(--ds-density-control-height)]"
            required
          />
          {expiredConfirmPassword && expiredNewPassword !== expiredConfirmPassword && (
            <p className="text-sm text-destructive" role="alert">
              {t("passwordExpired.mismatch", { defaultValue: "Passwords do not match" })}
            </p>
          )}
        </div>
        <div className="grid gap-2">
          <Label>{t("passwordExpired.expiration", { defaultValue: "Password expiration" })}</Label>
          <Select value={expiredPeriod} onValueChange={setExpiredPeriod}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRATION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(`passwordExpired.periods.${opt.labelKey}`, { defaultValue: opt.value })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Button type="submit" className="h-[var(--ds-density-control-height)] w-full" disabled={isSubmitting || !expiredFormValid}>
            {isSubmitting
              ? t("passwordExpired.submitLoading", { defaultValue: "Updating…" })
              : t("passwordExpired.submit", { defaultValue: "Update password and continue" })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setPasswordExpiredChallenge(null);
              setExpiredCurrentPassword("");
              setExpiredNewPassword("");
              setExpiredConfirmPassword("");
            }}
          >
            {t("mfa.backButton", { defaultValue: "Back to login" })}
          </Button>
        </div>
      </form>
    );
  } else if (mfaChallenge) {
    stepContent = (
      <form onSubmit={handleMfaVerify} className="flex flex-col gap-6">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("mfa.title", { defaultValue: "Two-factor verification" })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("mfa.description", {
              defaultValue: "Enter the code sent to {{destination}}",
              destination: mfaChallenge.maskedDestination || "…",
            })}
          </p>
        </div>

        {channelOptions.length > 1 && (
          <div className="grid gap-2">
            <Label>{t("mfa.channelLabel", { defaultValue: "Channel" })}</Label>
            <Select value={mfaChannel} onValueChange={setMfaChannel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {channelOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid gap-2">
          <Label htmlFor="mfa-code">{t("mfa.codeLabel", { defaultValue: "Verification code" })}</Label>
          <Input
            id="mfa-code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            className="h-[var(--ds-density-control-height)] tracking-widest"
            required
            autoFocus
          />
          <OtpCountdownHint
            ns="login"
            active={mfaCountdown.active}
            expired={mfaCountdown.expired}
            expiresLabel={mfaCountdown.expiresLabel}
            canResend={mfaCountdown.canResend}
            resendInSec={mfaCountdown.resendInSec}
            attemptsRemaining={mfaAttemptsRemaining}
            maxAttempts={mfaChallenge?.maxAttempts}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button type="submit" className="h-[var(--ds-density-control-height)] w-full" disabled={isSubmitting || !mfaCode.trim() || mfaCountdown.expired}>
            {isSubmitting
              ? t("mfa.verifyLoading", { defaultValue: "Verifying…" })
              : t("mfa.verifyButton", { defaultValue: "Verify" })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleMfaResend}
            disabled={isSubmitting || !mfaCountdown.canResend}
          >
            {t("mfa.resendButton", { defaultValue: "Resend code" })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setMfaChallenge(null);
              setMfaCode("");
              setMfaIssuedAt(null);
              setMfaAttemptsRemaining(null);
            }}
          >
            {t("mfa.backButton", { defaultValue: "Back to login" })}
          </Button>
        </div>
      </form>
    );
  } else {
    stepContent = (
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-6">
          {showSuccess && (
            <div className="rounded-[var(--ds-radius-control)] border border-success/35 bg-success/10 p-[var(--ds-space-3)] text-sm text-[var(--success-tint-foreground)]" role="alert">
              <div className="flex items-center">
                <div className="mr-2 size-4 animate-spin rounded-full border-b-2 border-success motion-reduce:animate-none" />
                {t("form.successMessage", { defaultValue: "Login successful! Redirecting to dashboard..." })}
              </div>
            </div>
          )}

          {(() => {
            const account = new URLSearchParams(location.search).get('account');
            if (account !== 'suspended' && account !== 'inactive') return null;
            const key = account === 'suspended' ? 'errors.accountSuspended' : 'errors.accountInactive';
            return (
              <div className="rounded-[var(--ds-radius-control)] border border-destructive/45 bg-destructive/10 p-[var(--ds-space-3)] text-sm text-[var(--destructive-tint-foreground)]" role="alert">
                {t(key)}
              </div>
            );
          })()}

          {error && (
            <div className="rounded-[var(--ds-radius-control)] border border-destructive/45 bg-destructive/10 p-[var(--ds-space-3)] text-sm text-[var(--destructive-tint-foreground)]" role="alert">
              {t(error, { defaultValue: error })}
            </div>
          )}

          <div className="grid gap-2.5">
            <Label htmlFor="email" className="text-sm">
              {t("form.emailLabel", { defaultValue: "Email" })}
            </Label>
            <Input
              id="email"
              name="email"
              type="text"
              placeholder={t("form.emailPlaceholder", { defaultValue: "Enter your email" })}
              value={formData.email}
              onChange={handleInputChange}
              className={cn(
"h-[var(--ds-density-control-height)] rounded-[var(--ds-radius-control)]",
                formErrors.email ? "border-destructive focus-visible:ring-destructive/40" : "",
              )}
              required
              autoFocus
            />
            {formErrors.email && (
              <p className="text-sm text-destructive" role="alert">
                {t(formErrors.email, { defaultValue: formErrors.email })}
              </p>
            )}
          </div>

          <div className="grid gap-2.5">
            <div className="flex items-center">
              <Label htmlFor="password" className="text-sm">
                {t("form.passwordLabel", { defaultValue: "Password" })}
              </Label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="ml-auto inline-block text-xs underline-offset-4 hover:underline text-muted-foreground hover:text-foreground"
              >
                {t("form.forgotPasswordLink", { defaultValue: "Forgot your password?" })}
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                className={cn(
"h-[var(--ds-density-control-height)] rounded-[var(--ds-radius-control)] pr-10",
                  formErrors.password ? "border-destructive focus-visible:ring-destructive/40" : "",
                )}
                placeholder={t("form.passwordPlaceholder", { defaultValue: "Enter your password" })}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-[var(--ds-space-3)] top-1/2 flex size-[var(--ds-density-icon-button-size-compact)] -translate-y-1/2 items-center justify-center rounded-[var(--ds-radius-control-compact)] text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
                aria-label={showPassword ? t("form.hidePassword", { defaultValue: "Hide password" }) : t("form.showPassword", { defaultValue: "Show password" })}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {formErrors.password && (
              <p className="text-sm text-destructive" role="alert">
                {t(formErrors.password, { defaultValue: formErrors.password })}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="login-btn h-[var(--ds-density-control-height)] w-full cursor-pointer"
            disabled={isSubmitting || status === "loading"}
          >
            {isSubmitting || status === "loading"
              ? t("form.submitButtonLoading", { defaultValue: "Signing in..." })
              : t("form.submitButtonDefault", { defaultValue: "Sign In" })}
          </Button>
          {ssoProviders.map((provider) => (
            <Button
              key={`${provider.tenantSlug}-${provider.key}`}
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                window.location.assign(`${api.origin}/idp/${provider.tenantSlug}/${provider.key}/oidc/start`);
              }}
            >
              {t('sso.signInWith', { name: provider.name, defaultValue: 'Sign in with {{name}}' })}
            </Button>
          ))}
        </div>
      </form>
    );
  }

  return (
    <div className={cn("login-container flex flex-col gap-8", className)} {...props}>
      <Card className="login-card">
        <CardHeader className="text-center space-y-1">
          <div className="login-logo-container mb-3">
            <img
              src={cyberverseLogo}
              alt={t("form.logoAlt", { defaultValue: "Cyberverse Technology Inc." })}
              className="mx-auto h-32 w-auto"
            />
          </div>
          {onMobileSwitch && (
            <Button
              variant="link"
              className="lg:hidden text-primary w-full mb-2 h-auto whitespace-normal text-center"
              onClick={onMobileSwitch}
            >
              {t("page.viewPublicDocuments", { defaultValue: "Public documents & breach notification →" })}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {stepContent}
        </CardContent>
      </Card>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
}
